import { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { TableSkeleton } from '../../lib/skeletons';
import { toast } from 'sonner';
import {
  Briefcase, CheckCircle2, XCircle, RefreshCw, Plus, Trash2,
  ChevronDown, ChevronRight, Loader2, AlertTriangle,
} from 'lucide-react';
import { fmtFecha } from '../../lib/utils'; // Iter 292.g — formato DD-MM-YYYY

const fmtCLP = (n) => (Number(n) || 0).toLocaleString('es-CL');
const ESTADO_LABEL = {
  pendiente_vb: { txt: 'Pendiente VB', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  aprobado: { txt: 'Aprobado', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  rechazado: { txt: 'Rechazado', cls: 'bg-red-100 text-red-800 border-red-300' },
};

export default function LotesServiciosPage() {
  const { user } = useAuth();
  const role = (user?.rol || user?.role || '').toLowerCase();
  const canVB = ['admin', 'administrador', 'gerencia'].includes(role);
  const canCrear = canVB || role === 'contabilidad';

  const [data, setData] = useState({ lotes: [], kpis: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pendiente_vb');
  const [expanded, setExpanded] = useState({});

  // Crear lote
  const [crearDlg, setCrearDlg] = useState(false);
  const [candidatas, setCandidatas] = useState([]);
  const [loadingCand, setLoadingCand] = useState(false);
  const [selected, setSelected] = useState({});
  const [nombre, setNombre] = useState('');
  const [obsCrear, setObsCrear] = useState('');
  const [creando, setCreando] = useState(false);

  // VB Gerencia
  const [vbDlg, setVbDlg] = useState(null); // {lote, action: 'aprobar'|'rechazar'}
  const [obsVB, setObsVB] = useState('');
  const [processing, setProcessing] = useState(false);

  // Eliminar
  const [delLote, setDelLote] = useState(null);

  const fetchLotes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/contabilidad/lotes-servicios');
      setData(res.data || { lotes: [], kpis: {}, total: 0 });
    } catch { toast.error('Error al cargar lotes de servicios'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchLotes(); }, [fetchLotes]);

  const fetchCandidatas = useCallback(async () => {
    setLoadingCand(true);
    try {
      const res = await api.get('/contabilidad/lotes-servicios/candidatos');
      setCandidatas(res.data?.candidatas || []);
    } catch { toast.error('Error al cargar candidatas'); }
    finally { setLoadingCand(false); }
  }, []);

  const openCrear = async () => {
    setCrearDlg(true);
    setSelected({});
    const defaultName = `Servicios ${new Date().toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-')}`;
    setNombre(defaultName);
    setObsCrear('');
    await fetchCandidatas();
  };

  const totalSel = useMemo(() => {
    return candidatas
      .filter((c) => selected[c.alegra_id])
      .reduce((acc, c) => ({ n: acc.n + 1, monto: acc.monto + (c.monto || 0) }), { n: 0, monto: 0 });
  }, [candidatas, selected]);

  const crearLote = async () => {
    const facturas = candidatas.filter((c) => selected[c.alegra_id]).map((c) => ({
      alegra_id: c.alegra_id,
      folio: c.folio || '',
      razon_social: c.razon_social || '',
      rut_proveedor: c.rut_proveedor || '',
      monto: c.monto || 0,
      fecha_emision: c.fecha_emision || '',
      fecha_vencimiento: c.fecha_vencimiento || '',
    }));
    if (facturas.length === 0) { toast.error('Selecciona al menos 1 factura'); return; }
    if (!nombre.trim() || nombre.trim().length < 3) { toast.error('Nombre del lote (≥3 chars)'); return; }
    setCreando(true);
    try {
      const res = await api.post('/contabilidad/lotes-servicios', {
        nombre: nombre.trim(), facturas, observacion: obsCrear.trim(),
      });
      toast.success(res.data?.message || 'Lote creado');
      setCrearDlg(false);
      await fetchLotes();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al crear lote');
    } finally { setCreando(false); }
  };

  const procesarVB = async () => {
    if (!vbDlg) return;
    const { lote, action } = vbDlg;
    if (action === 'rechazar' && (obsVB || '').trim().length < 5) {
      toast.error('Motivo de rechazo (≥5 chars)'); return;
    }
    setProcessing(true);
    try {
      const url = `/contabilidad/lotes-servicios/${lote.lote_id}/${action}`;
      const payload = action === 'aprobar' ? { observacion: obsVB.trim() } : { motivo: obsVB.trim() };
      const res = await api.post(url, payload);
      toast.success(res.data?.message || `Lote ${action === 'aprobar' ? 'aprobado' : 'rechazado'}`);
      setVbDlg(null); setObsVB('');
      await fetchLotes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Error al ${action}`);
    } finally { setProcessing(false); }
  };

  const eliminarLote = async () => {
    if (!delLote) return;
    try {
      await api.delete(`/contabilidad/lotes-servicios/${delLote.lote_id}`);
      toast.success('Lote eliminado');
      setDelLote(null);
      await fetchLotes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al eliminar');
    }
  };

  const lotesFiltrados = (data.lotes || []).filter((l) => tab === 'todos' || l.estado === tab);

  // UX: si el usuario no es admin/gerencia/contabilidad, mostrar mensaje explícito.
  if (!canCrear) {
    return (
      <div className="p-4" data-testid="lotes-servicios-page">
        <Card>
          <CardContent className="py-12 text-center space-y-2" data-testid="acceso-denegado">
            <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto" />
            <h2 className="text-lg font-semibold">Acceso restringido</h2>
            <p className="text-sm text-muted-foreground">
              Esta sección está disponible solo para usuarios con rol Administración, Gerencia o Contabilidad.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4" data-testid="lotes-servicios-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Briefcase className="w-6 h-6 text-purple-600" />
            Lotes Servicios
          </h1>
          <p className="text-sm text-muted-foreground">
            Agrupa facturas de servicios para VB de Gerencia. Al aprobar el lote, las facturas se liberan para pago.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchLotes} data-testid="btn-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> Refrescar
          </Button>
          {canCrear && (
            <Button size="sm" onClick={openCrear} data-testid="btn-crear-lote">
              <Plus className="w-4 h-4 mr-1" /> Nuevo Lote
            </Button>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Pendientes VB" valueN={data.kpis?.pendientes_vb || 0} valueMonto={data.kpis?.monto_pendiente_vb || 0}
          icon={<AlertTriangle className="w-4 h-4 text-amber-600" />} testid="kpi-pendientes" />
        <KpiCard label="Aprobados" valueN={data.kpis?.aprobados || 0} valueMonto={data.kpis?.monto_aprobado || 0}
          icon={<CheckCircle2 className="w-4 h-4 text-emerald-600" />} testid="kpi-aprobados" />
        <KpiCard label="Rechazados" valueN={data.kpis?.rechazados || 0} icon={<XCircle className="w-4 h-4 text-red-600" />} testid="kpi-rechazados" />
        <KpiCard label="Total Lotes" valueN={data.total || 0} icon={<Briefcase className="w-4 h-4 text-purple-600" />} testid="kpi-total" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="pendiente_vb" data-testid="tab-pendiente">Pendientes VB ({data.kpis?.pendientes_vb || 0})</TabsTrigger>
          <TabsTrigger value="aprobado" data-testid="tab-aprobados">Aprobados ({data.kpis?.aprobados || 0})</TabsTrigger>
          <TabsTrigger value="rechazado" data-testid="tab-rechazados">Rechazados ({data.kpis?.rechazados || 0})</TabsTrigger>
          <TabsTrigger value="todos" data-testid="tab-todos">Todos</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-3">
          {loading ? (
            <TableSkeleton rows={5} cols={5} />
          ) : lotesFiltrados.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-muted-foreground" data-testid="empty-state">
              No hay lotes {ESTADO_LABEL[tab]?.txt?.toLowerCase() || tab}.
            </CardContent></Card>
          ) : (
            <div className="space-y-2">
              {lotesFiltrados.map((lote) => (
                <LoteCard
                  key={lote.lote_id}
                  lote={lote}
                  expanded={!!expanded[lote.lote_id]}
                  onToggle={() => setExpanded((p) => ({ ...p, [lote.lote_id]: !p[lote.lote_id] }))}
                  onVB={canVB ? (action) => { setVbDlg({ lote, action }); setObsVB(''); } : null}
                  onDelete={canCrear && lote.estado === 'pendiente_vb' ? () => setDelLote(lote) : null}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialog Crear Lote */}
      <Dialog open={crearDlg} onOpenChange={setCrearDlg}>
        <DialogContent className="max-w-3xl" data-testid="dlg-crear">
          <DialogHeader>
            <DialogTitle>Nuevo Lote de Servicios</DialogTitle>
            <DialogDescription>
              Selecciona las facturas de servicios que enviarás a VB de Gerencia.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nombre del lote</Label>
                <Input value={nombre} onChange={(e) => setNombre(e.target.value)}
                  placeholder="Ej: Servicios Feb-2026" data-testid="input-nombre" />
              </div>
              <div>
                <Label>Observación (opcional)</Label>
                <Input value={obsCrear} onChange={(e) => setObsCrear(e.target.value)}
                  data-testid="input-obs-crear" />
              </div>
            </div>

            <div className="border rounded">
              <div className="px-3 py-2 bg-muted flex items-center justify-between text-sm">
                <span>Candidatas disponibles ({candidatas.length})</span>
                <span className="text-purple-700 font-semibold">
                  Seleccionadas: {totalSel.n} · ${fmtCLP(totalSel.monto)}
                </span>
              </div>
              <div className="max-h-80 overflow-auto">
                {loadingCand ? (
                  <div className="p-8 text-center text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Cargando…</div>
                ) : candidatas.length === 0 ? (
                  <div className="p-6 text-center text-sm text-muted-foreground" data-testid="no-candidatas">
                    No hay facturas de servicios bloqueadas pendientes. Vuelve cuando llegue una factura tipo "Servicio" no liberada.
                  </div>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50 sticky top-0">
                      <tr>
                        <th className="px-2 py-1.5 w-8"></th>
                        <th className="px-2 py-1.5 text-left">Folio</th>
                        <th className="px-2 py-1.5 text-left">Proveedor</th>
                        <th className="px-2 py-1.5 text-right">Monto</th>
                        <th className="px-2 py-1.5 text-center">Emisión</th>
                        <th className="px-2 py-1.5 text-center">Vencimiento</th>
                      </tr>
                    </thead>
                    <tbody>
                      {candidatas.map((c) => (
                        <tr key={c.alegra_id} className="border-t hover:bg-muted/30">
                          <td className="px-2 py-1.5">
                            <Checkbox
                              checked={!!selected[c.alegra_id]}
                              onCheckedChange={(v) => setSelected((p) => ({ ...p, [c.alegra_id]: !!v }))}
                              data-testid={`chk-candidata-${c.alegra_id}`}
                            />
                          </td>
                          <td className="px-2 py-1.5 font-mono">{c.folio || '-'}</td>
                          <td className="px-2 py-1.5 truncate max-w-[200px]">{c.razon_social || '-'}</td>
                          <td className="px-2 py-1.5 text-right font-mono">${fmtCLP(c.monto)}</td>
                          <td className="px-2 py-1.5 text-center text-[11px]">{fmtFecha(c.fecha_emision) || '-'}</td>
                          <td className="px-2 py-1.5 text-center text-[11px]">{c.fecha_vencimiento || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCrearDlg(false)}>Cancelar</Button>
            <Button onClick={crearLote} disabled={creando || totalSel.n === 0} data-testid="btn-crear-confirm">
              {creando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
              Crear lote ({totalSel.n})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog VB (aprobar/rechazar) */}
      <Dialog open={!!vbDlg} onOpenChange={(o) => !o && setVbDlg(null)}>
        <DialogContent data-testid="dlg-vb">
          <DialogHeader>
            <DialogTitle>
              {vbDlg?.action === 'aprobar' ? 'Aprobar Lote (VB Gerencia)' : 'Rechazar Lote'}
            </DialogTitle>
            <DialogDescription>
              {vbDlg?.action === 'aprobar'
                ? `Al aprobar, las ${vbDlg?.lote?.total_facturas || 0} factura(s) ($${fmtCLP(vbDlg?.lote?.total_monto)}) quedarán liberadas para pago.`
                : 'El motivo de rechazo quedará registrado. Las facturas permanecen bloqueadas.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>{vbDlg?.action === 'aprobar' ? 'Observación (opcional)' : 'Motivo de rechazo (≥5 chars)'}</Label>
            <Textarea
              value={obsVB}
              onChange={(e) => setObsVB(e.target.value)}
              placeholder={vbDlg?.action === 'aprobar' ? 'Comentario para auditoría…' : 'Explica por qué se rechaza el lote'}
              data-testid="textarea-vb"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVbDlg(null)}>Cancelar</Button>
            <Button
              variant={vbDlg?.action === 'aprobar' ? 'default' : 'destructive'}
              onClick={procesarVB}
              disabled={processing}
              data-testid="btn-vb-confirm"
            >
              {processing && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              {vbDlg?.action === 'aprobar' ? 'Aprobar y liberar pagos' : 'Rechazar lote'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Eliminar */}
      <Dialog open={!!delLote} onOpenChange={(o) => !o && setDelLote(null)}>
        <DialogContent data-testid="dlg-eliminar">
          <DialogHeader>
            <DialogTitle>Eliminar lote</DialogTitle>
            <DialogDescription>
              ¿Eliminar el lote "{delLote?.nombre}" con {delLote?.total_facturas} factura(s)?
              Las facturas vuelven a estar disponibles para otro lote.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelLote(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={eliminarLote} data-testid="btn-del-confirm">
              <Trash2 className="w-4 h-4 mr-1" /> Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiCard({ label, valueN, valueMonto, icon, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-muted-foreground">{label}</span>
          {icon}
        </div>
        <div className="text-2xl font-bold tabular-nums">{valueN}</div>
        {valueMonto != null && (
          <div className="text-xs text-muted-foreground tabular-nums">${fmtCLP(valueMonto)}</div>
        )}
      </CardContent>
    </Card>
  );
}

function LoteCard({ lote, expanded, onToggle, onVB, onDelete }) {
  const meta = ESTADO_LABEL[lote.estado] || { txt: lote.estado, cls: 'bg-gray-100 text-gray-700' };
  return (
    <Card data-testid={`lote-card-${lote.lote_id}`}>
      <CardContent className="p-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-2 flex-1 min-w-[200px]">
            <button onClick={onToggle} className="mt-1" data-testid={`toggle-${lote.lote_id}`}>
              {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            <div>
              <div className="font-semibold flex items-center gap-2">
                {lote.nombre}
                <span className={`text-[10px] px-2 py-0.5 rounded border ${meta.cls}`}>{meta.txt}</span>
              </div>
              <div className="text-xs text-muted-foreground">
                {lote.total_facturas} factura(s) · ${fmtCLP(lote.total_monto)} ·
                creado por {lote.creado_por} el {(lote.fecha_creacion || '').slice(0, 10)}
                {lote.fecha_vb && ` · VB ${(lote.fecha_vb || '').slice(0, 10)} por ${lote.vb_por}`}
              </div>
              {lote.observacion && <div className="text-xs italic mt-1">{lote.observacion}</div>}
              {lote.vb_observacion && (
                <div className="text-xs mt-1 px-2 py-1 rounded bg-muted">
                  <span className="font-semibold">VB:</span> {lote.vb_observacion}
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-1">
            {onVB && lote.estado === 'pendiente_vb' && (
              <>
                <Button size="sm" variant="default" onClick={() => onVB('aprobar')} data-testid={`btn-aprobar-${lote.lote_id}`}>
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Aprobar
                </Button>
                <Button size="sm" variant="destructive" onClick={() => onVB('rechazar')} data-testid={`btn-rechazar-${lote.lote_id}`}>
                  <XCircle className="w-4 h-4 mr-1" /> Rechazar
                </Button>
              </>
            )}
            {onDelete && (
              <Button size="sm" variant="outline" onClick={onDelete} data-testid={`btn-del-${lote.lote_id}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        {expanded && (
          <div className="mt-3 border rounded overflow-auto" data-testid={`detalle-${lote.lote_id}`}>
            <table className="w-full text-xs">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-2 py-1.5 text-left">Folio</th>
                  <th className="px-2 py-1.5 text-left">Proveedor</th>
                  <th className="px-2 py-1.5 text-right">Monto</th>
                  <th className="px-2 py-1.5 text-center">Emisión</th>
                  <th className="px-2 py-1.5 text-center">Vencimiento</th>
                </tr>
              </thead>
              <tbody>
                {(lote.facturas || []).map((f) => (
                  <tr key={f.factura_id || f.alegra_id} className="border-t">
                    <td className="px-2 py-1.5 font-mono">{f.folio || '-'}</td>
                    <td className="px-2 py-1.5 truncate max-w-[260px]">{f.razon_social || '-'}</td>
                    <td className="px-2 py-1.5 text-right font-mono">${fmtCLP(f.monto)}</td>
                    <td className="px-2 py-1.5 text-center text-[11px]">{fmtFecha(f.fecha_emision) || '-'}</td>
                    <td className="px-2 py-1.5 text-center text-[11px]">{f.fecha_vencimiento || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
